/**
 * Identity variants — the extra names, addresses and holding references that
 * supplement a person's base record (LF-5566 PROV-21).
 *
 * Mirrors the backend `identity-variants` DTOs verbatim; per the frontend rule
 * "do not transform data from the backend", components bind to these shapes
 * directly. The owner is polymorphic over exactly two entities, matching the
 * `matter_id` XOR `contact_id` discrete-FK pair on every variant table:
 * a **matter** (the flattened deceased) or a **contact**.
 */

import type { QuoteSource } from '@/lib/services/ai-analysis/types';

export type IdentityVariantOwnerType = 'matter' | 'contact';

export type NameVariantRole =
  | 'legal_at_death'
  | 'will_name'
  | 'former_maiden'
  | 'married'
  | 'trading'
  | 'other_aka';

export type ReconciliationBasis =
  | 'formal_change_with_evidence'
  | 'informal_aka'
  | 'spelling_variant';

export type AddressVariantType = 'residential' | 'postal';

export type AssetIdentityReferenceType = 'SRN' | 'HIN' | 'account' | 'other';

export type VariantConfidence = 'high' | 'medium' | 'low';

/**
 * Per-row extraction provenance. **Every field is null on a manually-entered
 * variant** — that is exactly how the UI tells a lawyer-entered row from an
 * AI-extracted one, and the gate on rendering the "Informed by …" footnote.
 */
export interface VariantProvenance {
  sourceType: QuoteSource['type'] | null;
  quote: string | null;
  page: number | null;
  sourceFileId: number | null;
  /**
   * Resolved server-side from the source file — the stored value-object keeps
   * only the id, but the footnote should read "Informed by the will" rather
   * than "an uploaded document". Null when the file no longer resolves.
   */
  sourceFileName: string | null;
  sourceFileType: string | null;
  confidence: VariantConfidence | null;
  confirmedByAi: boolean | null;
}

export interface VariantAddressValue {
  businessName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  poBox?: string;
  country?: string;
}

interface VariantOwnerFields {
  /** Set when the variant belongs to a matter's deceased. */
  matterId: number | null;
  /** Set when the variant belongs to a contact. */
  contactId: number | null;
}

export interface NameVariant extends VariantOwnerFields {
  id: number;
  value: string;
  role: NameVariantRole;
  isPrimary: boolean;
  isCurrent: boolean;
  reconciliationBasis: ReconciliationBasis | null;
  provenance: VariantProvenance;
  createdAt: string;
  updatedAt: string;
}

export interface AddressVariant extends VariantOwnerFields {
  id: number;
  value: VariantAddressValue;
  type: AddressVariantType;
  isPrimary: boolean;
  isCurrent: boolean;
  periodNote: string | null;
  provenance: VariantProvenance;
  createdAt: string;
  updatedAt: string;
}

export interface AssetIdentityReference extends VariantOwnerFields {
  id: number;
  registryOrInstitution: string;
  referenceType: AssetIdentityReferenceType;
  value: string;
  provenance: VariantProvenance;
  createdAt: string;
  updatedAt: string;
}

export interface IdentityVariantsResponse {
  nameVariants: NameVariant[];
  addressVariants: AddressVariant[];
  assetIdentityReferences: AssetIdentityReference[];
}

// ── Request payloads ─────────────────────────────────────────────────────────

export interface CreateNameVariantRequest {
  value: string;
  role: NameVariantRole;
  isPrimary?: boolean;
  isCurrent?: boolean;
  reconciliationBasis?: ReconciliationBasis | null;
}

export type UpdateNameVariantRequest = Partial<CreateNameVariantRequest>;

export interface CreateAddressVariantRequest {
  value: VariantAddressValue;
  type: AddressVariantType;
  isPrimary?: boolean;
  isCurrent?: boolean;
  periodNote?: string | null;
}

export type UpdateAddressVariantRequest = Partial<CreateAddressVariantRequest>;

export interface CreateAssetIdentityReferenceRequest {
  registryOrInstitution: string;
  referenceType: AssetIdentityReferenceType;
  value: string;
}

export type UpdateAssetIdentityReferenceRequest =
  Partial<CreateAssetIdentityReferenceRequest>;

// ── Display vocabularies ─────────────────────────────────────────────────────

/**
 * Plain-English labels for the stored enums. Probate-facing wording, not the
 * raw taxonomy — a solicitor reads "Name in the will", never `will_name`.
 */
export const NAME_VARIANT_ROLE_LABELS: Record<NameVariantRole, string> = {
  legal_at_death: 'Legal name at death',
  will_name: 'Name in the will',
  former_maiden: 'Former / maiden name',
  married: 'Married name',
  trading: 'Trading name',
  other_aka: 'Also known as'
};

export const RECONCILIATION_BASIS_LABELS: Record<ReconciliationBasis, string> =
  {
    formal_change_with_evidence: 'Formal change (evidence to annexe)',
    informal_aka: 'Informal alias',
    spelling_variant: 'Spelling variant'
  };

export const ADDRESS_VARIANT_TYPE_LABELS: Record<AddressVariantType, string> = {
  residential: 'Residential',
  postal: 'Postal'
};

export const ASSET_IDENTITY_REFERENCE_TYPE_LABELS: Record<
  AssetIdentityReferenceType,
  string
> = {
  SRN: 'SRN',
  HIN: 'HIN',
  account: 'Account number',
  other: 'Other reference'
};

/** One-line rendering of a variant address for a list row. */
export function formatVariantAddress(value: VariantAddressValue): string {
  const suburbLine = [value.suburb, value.state, value.postcode]
    .filter(Boolean)
    .join(' ');
  return [value.businessName, value.poBox, value.streetAddress, suburbLine]
    .filter((part) => !!part && part.trim().length > 0)
    .join(', ');
}
